import { AddArrayEntryAction, RemoveArrayEntryAction } from 'Data/ReduxViewModels/DataSync/types';
import { Guid } from 'Lib/Guid';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import { InjectedPropsProvider } from '../../../Core/InjectedPropsContext';
import { getWorksheetProviderProps } from './getWorksheetProviderProps';
import { WorksheetInjectedConsumerProps, WorksheetInjectedPropsModifier } from './types';

/**
 * The intention of this class is to apply common logic to all child components that should work consistently across
 * worksheets components that opt into consumption, e.g.:
 *    - Certain conditions should disable all components.
 *    - Save all input onChange values as they are entered, usng the Web API.
 */
let WorksheetEditorInjectedPropsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();

  const locked = useSelector(
    (state) =>
      state.worksheetEditor.status.readonly.locked ||
      // Additional actions that can be performed in the worksheet editor that require it to be locked down
      state.psdSieveAnalysis.refreshSieves.status.submitting
  );

  const worksheetId = useSelector((state) => state.worksheetEditor.worksheetId) as Guid;
  const addArrayEntry = useCallback(
    (payload: AddArrayEntryAction['payload']) => dispatch(actions.addArrayEntry(payload)),
    [dispatch]
  );
  const removeArrayEntry = useCallback(
    (payload: RemoveArrayEntryAction['payload']) => dispatch(actions.removeArrayEntry(payload)),
    [dispatch]
  );

  const getProviderProps = useCallback(
    (consumerProps: WorksheetInjectedConsumerProps, modifiers: Array<WorksheetInjectedPropsModifier>) =>
      getWorksheetProviderProps(
        consumerProps,
        {
          worksheetId,
          locked,
          setValue: (update: any) => {
            dispatch(actions.setValue(update));
          },
          synchronise: () => {
            dispatch(actions.sync());
          },
          worksheetSelector: (state) => state.worksheetEditor?.syncModel,
          worksheetConfigSelector: (state) => state.worksheetEditor?.config,
          worksheetTypeSelector: (state) => state.worksheetEditor?.worksheetType,
          addArrayEntry,
          removeArrayEntry,
        },
        modifiers
      ),
    [locked, worksheetId, dispatch, addArrayEntry, removeArrayEntry]
  );

  return <InjectedPropsProvider getProviderProps={getProviderProps}>{children}</InjectedPropsProvider>;
};

export { WorksheetEditorInjectedPropsProvider };
export default WorksheetEditorInjectedPropsProvider;
